"use client";

import { X } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState, useRef } from "react";
import Loader from "../components/loader.tsx";
import { materialTypeMapper } from "../utils/mappers/material-type.mapper.ts";
import type { MaterialTypeEnum } from "@ns/types";
import { useNavigate } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import { type SearchSchema, searchSchema } from "../schemas/search.schema.ts";
import { useFormContext } from "react-hook-form";
import ControlledForm from "../components/form/controlled-form.tsx";
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "../components/ui/pagination.tsx";
import type { SingleCodebookResponse } from "@ns/api";
import SyncLoader from "react-spinners/SyncLoader";
import useUserStore from "../stores/user.store.ts";
import { HStack } from "@chakra-ui/react";

const Main = () => {
  const { watch, reset, register } = useFormContext<SearchSchema>();

  const { materialFilter, currentPage, setCurrentPage } = useUserStore();

  const search = watch("search") || "";

  // Keep track of the previous search value
  const prevSearchRef = useRef(search);

  // Use the stored page index from user store
  const [pageIndex, setPageIndex] = useState(currentPage);
  const pageSize = 21;

  // Update the store when page changes
  useEffect(() => {
    setCurrentPage(pageIndex);
  }, [pageIndex, setCurrentPage]);

  // Reset page index ONLY when search actually changes
  useEffect(() => {
    if (prevSearchRef.current !== search) {
      setPageIndex(0);
      setCurrentPage(0);
      prevSearchRef.current = search;
    }
  }, [search, setCurrentPage]);

  // Keep track of previous material filter
  const prevMaterialFilterRef = useRef(materialFilter);

  // Reset page index ONLY when material filter actually changes
  useEffect(() => {
    if (prevMaterialFilterRef.current !== materialFilter) {
      setPageIndex(0);
      setCurrentPage(0);
      prevMaterialFilterRef.current = materialFilter;
    }
  }, [materialFilter, setCurrentPage]);

  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery(
    ["codebook", pageIndex, pageSize, search, materialFilter],
    () =>
      api.codebook.getCodebook(
        pageSize,
        pageIndex * pageSize,
        search,
        materialFilter ?? undefined,
        "true",
      ),
    {
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    refetch();
  }, [materialFilter, refetch]);

  const onNextPage = () => {
    if (canNextPage) {
      setPageIndex((prev) => prev + 1);
    }
  };
  const onPreviousPage = () => {
    if (canPreviousPage) {
      setPageIndex((prev) => prev - 1);
    }
  };
  const canPreviousPage = pageIndex > 0;
  const canNextPage = data ? pageIndex < data.totalPages - 1 : false;

  const clearSearch = () => {
    reset({ search: "" });
  };

  const handleOnClick = useCallback(
    (id: string) => {
      // Don't reset material filter here, just navigate
      navigate(`/${id}`);
    },
    [navigate],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-auto">
      <div className="relative mb-6 mt-4 mr-4 ml-4 md:w-64 flex items-center md:items-right md:ml-auto">
        <input
          type="text"
          placeholder="Pretrazi po nazivu"
          className="w-full text-black p-3 pr-10 border border-[#d9d9d9] bg-white rounded-[8px]"
          {...register("search")}
        />
        {search && (
          <button
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#b2b2b2]"
            onClick={clearSearch}
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 p-4 md:grid-cols-3 gap-4 md:gap-6 overflow-auto">
        {data?.items?.map((item) => (
          <div
            key={item.codebook.id}
            className="overflow-hidden bg-white rounded-lg shadow cursor-pointer transition-transform hover:scale-[1.02]"
            onClick={() => handleOnClick(item.codebook.id)}
          >
            <SingleElement item={item} />
          </div>
        ))}

        {!isLoading && data?.items?.length === 0 && (
          <div className="text-center py-10">
            <p className="text-lg text-[#b2b2b2]">Nema rezultata za pretragu</p>
          </div>
        )}
      </div>

      {!isLoading && data && data.totalPages > 1 && (
        <PaginationRoot
          count={data!.totalItems}
          pageSize={pageSize}
          page={pageIndex + 1}
          siblingCount={1}
          defaultPage={pageIndex + 1}
        >
          <HStack wrap="wrap" justify="center">
            <PaginationPrevTrigger
              onClick={onPreviousPage}
              disabled={!canPreviousPage}
            />
            <PaginationItems />
            <PaginationNextTrigger
              onClick={onNextPage}
              disabled={!canNextPage}
            />
          </HStack>
        </PaginationRoot>
      )}
    </div>
  );
};

const SingleElement = ({ item }: { item: SingleCodebookResponse }) => {
  const { mutate, data, isLoading } = useMutation({
    mutationFn: (url: string) => api.upload.getFileUrl({ url }),
  });

  useEffect(() => {
    if (item?.codebookFiles[0]?.file?.url) {
      mutate(item?.codebookFiles[0]?.file.url);
    }
  }, [item, mutate]);

  return (
    <>
      <div className="flex justify-between">
        <span className="inline-block px-3 py-1 text-xs text-white bg-[#1e1e1e] rounded-[4px]">
          {materialTypeMapper(
            item?.codebook?.codebookWarehouse?.type as MaterialTypeEnum,
          )}
        </span>
        <span className="inline-block px-3 py-1 text-xs text-[#1e1e1e] bg-[#d9d9d9] rounded-[4px]">
          {item?.codebook?.codebookWarehouse?.code}
        </span>
      </div>
      <div>
        {isLoading ? (
          <div
            style={{ width: "400px", height: "200px" }}
            className="justify-center flex items-center"
          >
            <SyncLoader />
          </div>
        ) : (
          <img
            src={!data?.url ? "/assets/placeholder.svg" : data?.url}
            alt={item?.codebookFiles[0]?.id}
            width={400}
            height={200}
            className="w-full h-48 object-cover"
          />
        )}
      </div>
      <div className="p-3 text-center">
        <h3 className="text-lg font-medium text-[#2c2c2c]">
          {item?.codebook?.codebookWarehouse?.name}
        </h3>
        <p className="text-sm text-[#b2b2b2]">{`${item.codebook.height}×${item.codebook.width}cm`}</p>
      </div>
    </>
  );
};

export const MainPage = () => {
  return (
    <ControlledForm schema={searchSchema}>
      <Main />
    </ControlledForm>
  );
};
